/* Debt Strategist. Scratchpad: nothing is saved, it hands its answer to the calculator's debt step. */
import React, { useMemo, useState } from 'react';
import { Plus, Trash as Trash2 } from "@phosphor-icons/react";
import { PitCrewToolShell, ToolLabel, ToolInput, ToolApplyBar, COLORS } from './PitCrewToolShell';

const { INK } = COLORS;
const blank = () => ({ id: Math.random().toString(36).slice(2), name: '', balance: '', apr: '', minPayment: '' });

function simulate(rows, extraMonthly, method) {
  const live = rows
    .map((r) => ({ id: r.id, name: r.name || 'Unnamed debt', bal: parseFloat(r.balance) || 0, apr: parseFloat(r.apr) || 0, min: parseFloat(r.minPayment) || 0 }))
    .filter((r) => r.bal > 0);
  if (!live.length) return null;

  const pool = live.reduce((s, r) => s + r.min, 0) + (parseFloat(extraMonthly) || 0);
  const startingBalance = live.reduce((s, r) => s + r.bal, 0);
  if (pool <= 0) return { stalled: true, startingBalance };

  const order = [];
  let interest = 0;
  let paidIn12 = 0;
  let month = 0;
  while (live.some((r) => r.bal > 0) && month < 600) {
    month += 1;
    live.forEach((r) => {
      if (r.bal > 0) {
        const i = r.bal * (r.apr / 100) / 12;
        r.bal += i;
        interest += i;
      }
    });
    let left = pool;
    live.forEach((r) => {
      if (r.bal > 0 && left > 0) {
        const p = Math.min(r.min, r.bal, left);
        r.bal -= p;
        left -= p;
        if (month <= 12) paidIn12 += p;
      }
    });
    const focus = live
      .filter((r) => r.bal > 0)
      .sort((a, b) => (method === 'snowball' ? a.bal - b.bal : b.apr - a.apr));
    for (const r of focus) {
      if (left <= 0.005) break;
      const p = Math.min(left, r.bal);
      r.bal -= p;
      left -= p;
      if (month <= 12) paidIn12 += p;
    }
    live.forEach((r) => {
      if (r.bal <= 0.005 && !order.some((o) => o.id === r.id)) {
        r.bal = 0;
        order.push({ id: r.id, name: r.name, month });
      }
    });
    if (month > 1 && live.every((r) => r.bal > 0) && pool <= interest / month) break;
  }

  const cleared = live.every((r) => r.bal <= 0.005);
  return {
    stalled: !cleared && month >= 600,
    startingBalance,
    monthlyPayment: pool,
    months: month,
    interest,
    paidIn12,
    order: order.sort((a, b) => a.month - b.month),
  };
}

const DebtStrategistModal = ({ onApply, onClose, formatCurrency }) => {
  const [rows, setRows] = useState([blank()]);
  const [extra, setExtra] = useState('');
  const [method, setMethod] = useState('snowball');

  const result = useMemo(() => simulate(rows, extra, method), [rows, extra, method]);
  const update = (id, field, value) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const applyMonths = result && !result.stalled ? Math.min(result.months, 12) : 12;
  const applyAmount = result && !result.stalled
    ? Math.round(result.months <= 12 ? result.startingBalance + result.interest : result.paidIn12)
    : 0;

  return (
    <PitCrewToolShell
      eyebrow="Pit Crew tool"
      title="Debt Strategist"
      lead="List what you owe, pick your order, and see the payoff date. Nothing here is saved, it is a scratchpad that hands its answer to your debt step."
      onClose={onClose}
      testId="debt-strategist"
    >
      <div className="space-y-3 mb-5">
        <div className="hidden sm:grid grid-cols-[1.4fr_1fr_0.7fr_1fr_auto] gap-3">
          <ToolLabel>Debt</ToolLabel>
          <ToolLabel>Balance</ToolLabel>
          <ToolLabel>APR</ToolLabel>
          <ToolLabel>Minimum / mo</ToolLabel>
          <span />
        </div>
        {rows.map((r, i) => (
          <div key={r.id} className="grid sm:grid-cols-[1.4fr_1fr_0.7fr_1fr_auto] gap-3 items-center">
            <ToolInput value={r.name} onChange={(e) => update(r.id, 'name', e.target.value)} placeholder={`Debt ${i + 1}`} data-testid={`debt-row-name-${i}`} />
            <ToolInput type="number" prefix="$" value={r.balance} onChange={(e) => update(r.id, 'balance', e.target.value)} placeholder="4,000" data-testid={`debt-row-balance-${i}`} />
            <ToolInput type="number" suffix="%" value={r.apr} onChange={(e) => update(r.id, 'apr', e.target.value)} placeholder="19" data-testid={`debt-row-apr-${i}`} />
            <ToolInput type="number" prefix="$" value={r.minPayment} onChange={(e) => update(r.id, 'minPayment', e.target.value)} placeholder="120" data-testid={`debt-row-min-${i}`} />
            <button
              onClick={() => setRows((prev) => (prev.length === 1 ? [blank()] : prev.filter((x) => x.id !== r.id)))}
              className="p-2 transition-opacity hover:opacity-60 justify-self-start"
              style={{ color: '#8B7E6A' }}
              aria-label="Remove debt"
              data-testid={`debt-row-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          onClick={() => setRows((prev) => [...prev, blank()])}
          className="font-body text-sm inline-flex items-center gap-1.5 transition-opacity hover:opacity-70"
          style={{ color: INK }}
          data-testid="debt-add-row-btn"
        >
          <Plus className="w-4 h-4" /> Add another debt
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <ToolLabel>Extra you can put in each month</ToolLabel>
          <ToolInput type="number" prefix="$" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="200" data-testid="debt-extra-input" />
        </div>
        <div>
          <ToolLabel>Payoff order</ToolLabel>
          <div className="flex gap-2">
            {[
              { id: 'snowball', label: 'Snowball', hint: 'smallest balance first' },
              { id: 'avalanche', label: 'Avalanche', hint: 'highest rate first' },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                className="flex-1 px-3 py-2.5 text-left transition-colors"
                style={{
                  background: method === m.id ? INK : '#FFFFFF',
                  border: `1px solid ${method === m.id ? INK : '#E0D9CB'}`,
                  borderRadius: 0,
                }}
                data-testid={`debt-method-${m.id}`}
              >
                <span className="font-body text-sm font-semibold block" style={{ color: method === m.id ? '#F2EFE7' : INK }}>{m.label}</span>
                <span className="font-body text-xs" style={{ color: method === m.id ? 'rgba(255,255,255,0.7)' : '#8B7E6A' }}>{m.hint}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {result && !result.stalled && (
        <div className="p-6" style={{ background: '#FFFFFF', border: '1px solid #E8E2D5' }} data-testid="debt-strategist-result">
          <div className="grid sm:grid-cols-3 gap-5 mb-5">
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Debt free in</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{result.months} month{result.months === 1 ? '' : 's'}</p>
            </div>
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Going in each month</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(result.monthlyPayment)}</p>
            </div>
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Interest you will pay</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(result.interest)}</p>
            </div>
          </div>
          <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: '#8B7E6A' }}>Payoff order</p>
          <ol className="space-y-1.5">
            {result.order.map((o, i) => (
              <li key={o.id} className="flex justify-between font-body text-sm" style={{ color: '#5D5A6B' }} data-testid={`debt-order-${i}`}>
                <span><b style={{ color: INK }}>{i + 1}.</b> {o.name}</span>
                <span>cleared in month {o.month}</span>
              </li>
            ))}
          </ol>
          {result.months > 12 && (
            <p className="font-body text-sm mt-5 pt-4 border-t leading-relaxed" style={{ color: '#5D5A6B', borderColor: '#E8E2D5' }}>
              This runs past a single race. Your calculator only plans 12 months at a time, so it will take
              the <b style={{ color: INK }}>{formatCurrency(result.paidIn12)}</b> you clear in the first 12 months.
            </p>
          )}
        </div>
      )}

      {result && result.stalled && (
        <div className="p-6" style={{ background: '#FFFFFF', border: '1px solid #E8E2D5' }} data-testid="debt-strategist-stalled">
          <p className="font-body text-sm leading-relaxed" style={{ color: '#5D5A6B' }}>
            At this payment the interest keeps pace with what you are paying, so the balance never clears.
            Add a little more each month and the payoff date will appear.
          </p>
        </div>
      )}

      <ToolApplyBar
        testId="debt-strategist"
        applyLabel={applyAmount > 0 ? `Use ${formatCurrency(applyAmount)} over ${applyMonths} months` : 'Use these numbers'}
        disabled={!applyAmount}
        note="This fills in your debt step, you can still change it."
        onApply={() => onApply({ debtAmount: String(applyAmount), debtMonths: String(applyMonths) })}
        onClose={onClose}
      />
    </PitCrewToolShell>
  );
};

export default DebtStrategistModal;
